import { useState } from "react";
import { toast } from "sonner";
import { Plus, Star, Trash2, ImageOff, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SortableList } from "@/components/SortableList";
import { errMsg } from "@/lib/api";

const HOSTS = [["Imgur", "https://imgur.com"], ["ImgBB", "https://imgbb.com"], ["Postimages", "https://postimages.org"], ["Flickr", "https://flickr.com"],
  ["DeviantArt", "https://deviantart.com"], ["ArtStation", "https://artstation.com"], ["Pinterest", "https://pinterest.com"], ["Wikimedia Commons", "https://commons.wikimedia.org"]];

export function SafeImg({ src, alt = "", className = "", testid }) {
  const [bad, setBad] = useState(false);
  if (bad) return <div className={`flex flex-col items-center justify-center gap-1 bg-[#0F131A] text-[10px] text-[#A8A090] ${className}`} title="This link isn't a direct image" data-testid={testid}><ImageOff className="h-4 w-4" />Not a direct image link</div>;
  return <img src={src} alt={alt} loading="lazy" referrerPolicy="no-referrer" onError={() => setBad(true)} className={`object-cover ${className}`} data-testid={testid} />;
}

export function HostTips() {
  return (
    <Popover>
      <PopoverTrigger asChild><button type="button" className="flex items-center gap-1 text-xs text-[#A8A090] hover:text-[#F59E0B]" data-testid="image-host-tips"><Info className="h-3.5 w-3.5" />Where to host images?</button></PopoverTrigger>
      <PopoverContent className="w-80 text-sm">
        <p className="mb-2 text-[#E8DFD1]">Upload your image to one of these, then right-click the image and choose <b>Copy image address</b>. Paste that link here.</p>
        <div className="flex flex-wrap gap-1.5">{HOSTS.map(([n, u]) => <a key={n} href={u} target="_blank" rel="noreferrer" className="rounded-sm border border-[#2D3442] px-2 py-0.5 text-xs text-[#F59E0B] hover:border-[#D97706]">{n}</a>)}</div>
        <p className="mt-2 text-xs text-[#A8A090]">Direct links usually end in .jpg, .png, .gif or .webp (e.g. i.imgur.com/abc123.jpg).</p>
      </PopoverContent>
    </Popover>
  );
}

function Lightbox({ images, index, setIndex }) {
  const img = images[index];
  if (!img) return null;
  const go = (d) => setIndex((index + d + images.length) % images.length);
  return (
    <Dialog open onOpenChange={() => setIndex(null)}>
      <DialogContent className="max-w-5xl border-[#2D3442] bg-[#0B0D10] p-4" data-testid="image-lightbox" onKeyDown={(e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); }}>
        <DialogTitle className="sr-only">Image {index + 1}</DialogTitle>
        <DialogDescription className="sr-only">{img.caption || "Image"}</DialogDescription>
        <div className="relative flex items-center justify-center">
          <SafeImg src={img.url} className="max-h-[75vh] w-auto max-w-full !object-contain" />
          {images.length > 1 && <>
            <Button size="icon" variant="ghost" className="absolute left-0" onClick={() => go(-1)} data-testid="lightbox-prev"><ChevronLeft className="h-6 w-6" /></Button>
            <Button size="icon" variant="ghost" className="absolute right-0" onClick={() => go(1)} data-testid="lightbox-next"><ChevronRight className="h-6 w-6" /></Button>
          </>}
        </div>
        <div className="flex justify-between text-sm"><span className="text-[#E8DFD1]" data-testid="lightbox-caption">{img.caption}</span><span className="font-mono-lf text-xs text-[#A8A090]">{index + 1} / {images.length}</span></div>
      </DialogContent>
    </Dialog>
  );
}

function AddImage({ onAdd }) {
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const add = async (e) => {
    e.preventDefault();
    if (!/^https?:\/\/\S+$/i.test(url.trim())) return toast.error("Paste a link starting with http:// or https://");
    if (await onAdd({ url: url.trim(), caption })) { setUrl(""); setCaption(""); }
  };
  return (
    <form onSubmit={add} className="mt-3 flex flex-wrap items-center gap-2" data-testid="image-add-form">
      <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://i.imgur.com/abc123.jpg" className="h-9 min-w-[240px] flex-1" data-testid="image-url-input" />
      <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)" className="h-9 w-48" data-testid="image-caption-input" />
      <Button type="submit" size="sm" disabled={!url.trim()} data-testid="image-add-button"><Plus className="h-3.5 w-3.5 mr-1" />Add image</Button>
      <HostTips />
    </form>
  );
}

export function ImageGallery({ images = [], canEdit, onSave, title = "Images" }) {
  const [view, setView] = useState(null);
  const save = async (next) => {
    try {
      await onSave(next.map(({ url, caption }) => ({ url, caption: caption || "" })));
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  if (!images.length && !canEdit) return null;
  const tile = (img, i) => (
    <div className="group relative" data-testid={`gallery-image-${i}`}>
      <button type="button" onClick={() => setView(i)} className="block w-full overflow-hidden rounded border border-[#2D3442]" data-testid={`gallery-open-${i}`}>
        <SafeImg src={img.url} alt={img.caption} className="h-32 w-full transition-transform duration-300 group-hover:scale-105" />
      </button>
      {i === 0 && <span className="absolute left-1.5 top-1.5 rounded-sm bg-[#0B0D10]/80 px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-[#F59E0B]" data-testid="gallery-cover-badge">Cover</span>}
      {img.caption && <p className="mt-1 truncate text-xs text-[#A8A090]">{img.caption}</p>}
      {canEdit && (
        <div className="absolute right-1 top-1 flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
          {i > 0 && <Button size="icon" variant="ghost" className="h-7 w-7 bg-[#0B0D10]/80" title="Make cover" onClick={() => save([img, ...images.filter((_, j) => j !== i)])} data-testid={`gallery-cover-${i}`}><Star className="h-3.5 w-3.5" /></Button>}
          <Button size="icon" variant="ghost" className="h-7 w-7 bg-[#0B0D10]/80" title="Remove" onClick={() => save(images.filter((_, j) => j !== i))} data-testid={`gallery-remove-${i}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
        </div>
      )}
    </div>
  );
  return (
    <section className="mt-8" data-testid="image-gallery">
      <div className="lf-label mb-3">{title} · {images.length}</div>
      {images.length === 0 && <p className="text-sm text-[#A8A090]">No images yet. Paste a link to an image hosted elsewhere.</p>}
      {canEdit ? (
        <SortableList scope="gallery" items={images} getKey={(img) => img.id || img.url} onReorder={save} render={tile} className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{images.map((img, i) => <div key={img.id || i}>{tile(img, i)}</div>)}</div>
      )}
      {canEdit && <AddImage onAdd={(img) => save([...images, img])} />}
      {view != null && <Lightbox images={images} index={view} setIndex={setView} />}
    </section>
  );
}
